import React, {useEffect, useMemo, useRef, useState} from 'react';
import {API_ENDPOINTS} from "../apiConfig";
import API from "../utils/API";
import Error from "./Error";
import Forms from "./Forms";
import {useTranslation} from 'react-i18next';
import {CONDITION_OPTIONS, QUALITY_OPTIONS} from "../utils/config.js";
import {getTranslatedDefaultOption, translateArrayKey} from "../utils/tools";

const TYPE_OPTIONS = [
    {value: 'sparePart', label: 'Náhradní díl'},
    {value: 'product', label: 'Produkt'}
];

const CURRENCY_OPTIONS = [
    {value: 'CZK', label: 'CZK'},
    {value: 'EUR', label: 'EUR'},
    {value: 'USD', label: 'USD'}
];

const toNumber = (v) => (v === '' || v === null || v === undefined) ? '' : Number(v);
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const slugifyCode = (value) => String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);

const PartModal = ({close, onPartUpdate, part}) => {
    const {t} = useTranslation();
    const inputRef = useRef(null);

    const [options, setOptions] = useState({categories: [], devices: []});
    const [loadingOpts, setLoadingOpts] = useState(true);

    const [form, setForm] = useState({
        name: '',
        description: '',
        stockPriority: 1,
        type: TYPE_OPTIONS[0],
        categoryList: [],
        compatibleWith: [],
        storagePlace: '',
        stockQuantity: 1,
        reorderThreshold: 1,
        condition: getTranslatedDefaultOption(CONDITION_OPTIONS, 'new', t),
        quality: getTranslatedDefaultOption(QUALITY_OPTIONS, 'original', t),
        expectedPurchasePricePerUnit: {currency: 'CZK', net: '', gross: '', vatRate: 21}
    });

    // která cena byla naposledy změněna uživatelem: 'net' | 'gross'
    const [priceSource, setPriceSource] = useState('net');

    const [error, setError] = useState();
    const isEdit = !!part?._id;
    const disableWhenEdit = isEdit;

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 1);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                setLoadingOpts(true);
                const [catRes, devRes] = await Promise.all([
                    API.get(API_ENDPOINTS.part.categories),
                    API.get(API_ENDPOINTS.device.list)
                ]);
                setOptions({
                    categories: (catRes.data || []).map(cat => ({value: cat._id, label: cat.name, code: cat.code})),
                    devices: (devRes.data || []).map(dev => ({value: dev._id, label: dev.name}))
                });
            } catch (e) {
                setError(e);
            } finally {
                setLoadingOpts(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (!part) return;
        const master = part.part || {};
        setForm(prev => ({
            ...prev,
            name: part.name || master.name || '',
            description: master.description || '',
            stockPriority: part.stockPriority || 1,
            type: TYPE_OPTIONS.find(x => x.value === master.type) || TYPE_OPTIONS[0],
            categoryList: (master.categoryList || []).map(cat => ({value: cat._id, label: cat.name})),
            compatibleWith: (master.compatibleWith || []).map(dev => ({value: dev._id, label: dev.name})),
            storagePlace: part.storagePlace || '',
            stockQuantity: part.stockQuantity ?? 1,
            reorderThreshold: part.reorderThreshold ?? 1,
            condition: getTranslatedDefaultOption(CONDITION_OPTIONS, part?.attributes?.lifecycleState === 'used' ? 'used' : 'new', t),
            quality: getTranslatedDefaultOption(QUALITY_OPTIONS, part?.attributes?.originType || 'original', t),
            expectedPurchasePricePerUnit: {
                currency: part.expectedPurchasePricePerUnit?.currency ?? 'CZK',
                net: toNumber(part.expectedPurchasePricePerUnit?.net ?? ''),
                gross: toNumber(part.expectedPurchasePricePerUnit?.gross ?? ''),
                vatRate: toNumber(part.expectedPurchasePricePerUnit?.vatRate ?? 21)
            }
        }));
        setPriceSource('net');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [part]);

    const setField = (name, value) => setForm(prev => ({...prev, [name]: value}));
    const setNumberField = (name, value) => setForm(prev => ({...prev, [name]: toNumber(value)}));

    const setExpectedPrice = (patch) => {
        setForm(prev => ({
            ...prev,
            expectedPurchasePricePerUnit: {...prev.expectedPurchasePricePerUnit, ...patch}
        }));
    };

    // Obousměrná kalkulace:
    // - změna NET -> dopočítej GROSS
    const onChangeNet = (v) => {
        const net = toNumber(v);
        const rate = Number(form.expectedPurchasePricePerUnit.vatRate) || 0;
        const gross = (net === '' ? '' : round2(Number(net) * (1 + rate / 100)));
        setExpectedPrice({net, gross});
        setPriceSource('net');
    };

    // - změna GROSS -> dopočítej NET
    const onChangeGross = (v) => {
        const gross = toNumber(v);
        const rate = Number(form.expectedPurchasePricePerUnit.vatRate) || 0;
        const divisor = (1 + rate / 100);
        const net = (gross === '' ? '' : round2(Number(gross) / divisor));
        setExpectedPrice({gross, net});
        setPriceSource('gross');
    };

    // - změna VAT -> přepočítej druhé pole podle posledního zdroje
    const onChangeVat = (v) => {
        const vatRate = toNumber(v);
        const {net, gross} = form.expectedPurchasePricePerUnit;
        if (priceSource === 'net') {
            const newGross = (net === '' ? '' : round2(Number(net) * (1 + Number(vatRate) / 100)));
            setExpectedPrice({vatRate, gross: newGross});
        } else {
            const divisor = (1 + Number(vatRate) / 100);
            const newNet = (gross === '' ? '' : round2(Number(gross) / divisor));
            setExpectedPrice({vatRate, net: newNet});
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(undefined);

        try {
            const trimmedName = form.name.trim();
            const masterPayload = {
                name: trimmedName,
                code: slugifyCode(trimmedName) || `PART-${Date.now()}`,
                categoryList: form.categoryList.map(x => x.value),
                compatibleWith: form.compatibleWith.map(x => x.value),
                description: form.description || '',
                type: form.type.value,
                variantSchema: [
                    {key: 'originType', label: 'Původ', type: 'enum', required: true, options: ['original', 'oem', 'aftermarket'], group: 'general', sortOrder: 10, active: true},
                    {key: 'lifecycleState', label: 'Stav', type: 'enum', required: true, options: ['new', 'used', 'refurbished', 'damaged'], group: 'general', sortOrder: 20, active: true},
                    {key: 'refurbSource', label: 'Zdroj repasu', type: 'enum', required: false, options: ['own', 'purchased'], group: 'general', sortOrder: 30, active: true}
                ]
            };
            const skuPayload = {
                name: trimmedName,
                sku: `${slugifyCode(trimmedName) || 'SKU'}-${Date.now().toString().slice(-6)}`,
                stockQuantity: Number(form.stockQuantity) || 0,
                reorderThreshold: Number(form.reorderThreshold) || 0,
                storagePlace: form.storagePlace || '',
                stockPriority: Number(form.stockPriority) || 0,
                expectedPurchasePricePerUnit: form.expectedPurchasePricePerUnit,
                attributes: {
                    originType: form.quality.value,
                    lifecycleState: form.condition.value === 'used' ? 'used' : 'new'
                }
            };

            if (isEdit) {
                const masterId = part?.part?._id || part?.part;
                if (masterId) {
                    await API.put(API_ENDPOINTS.part.edit(masterId), {
                        ...masterPayload,
                        code: part?.part?.code || masterPayload.code
                    });
                }
                await API.put(API_ENDPOINTS.partSku.edit(part._id), {
                    ...skuPayload,
                    part: masterId
                });
            } else {
                const createdMaster = await API.post(API_ENDPOINTS.part.add, masterPayload);
                await API.post(API_ENDPOINTS.partSku.add, {
                    ...skuPayload,
                    part: createdMaster?.data?._id
                });
            }

            await onPartUpdate?.();
            close();
        } catch (e) {
            setError(e);
        }
    };

    const conditionOpts = useMemo(() => translateArrayKey(CONDITION_OPTIONS, t), [t]);
    const qualityOpts = useMemo(() => translateArrayKey(QUALITY_OPTIONS, t), [t]);

    const fields = [
        {
            comp: Forms.Text, name: 'name', label: t('label'), required: true,
            props: {ref: inputRef, placeholder: t('label')}
        },
        {
            comp: Forms.Text, name: 'description', label: t('description'),
            props: {placeholder: t('description'), disabled: disableWhenEdit}
        },
        {
            comp: Forms.Select, name: 'type', label: t('type'), required: true,
            props: {options: TYPE_OPTIONS, disabled: disableWhenEdit}
        },
        {
            comp: Forms.Select, name: 'categoryList', label: t('category'), required: true,
            props: {multiple: true, options: options.categories, isLoading: loadingOpts, disabled: disableWhenEdit}
        },
        {
            comp: Forms.Select, name: 'compatibleWith', label: t('compatibleWith'),
            props: {multiple: true, options: options.devices, isLoading: loadingOpts}
        },
        {
            comp: Forms.Text, name: 'storagePlace', label: t('storagePlace'), required: true,
            props: {placeholder: t('storagePlace')}
        },
        {
            comp: Forms.Number, name: 'stockQuantity', label: t('stockQuantity'), required: true,
            props: {
                type: 'number', placeholder: t('stockQuantity'),
                onChange: (v) => setNumberField('stockQuantity', v)
            }
        },
        {
            comp: Forms.Number, name: 'stockPriority', label: t('stockPriority'), required: true,
            props: {
                type: 'number', placeholder: t('stockPriority'), disabled: disableWhenEdit,
                onChange: (v) => setNumberField('stockPriority', v)
            }
        },
        {
            comp: Forms.Number, name: 'reorderThreshold', label: t('reorderThreshold'), required: true,
            props: {
                type: 'number', placeholder: t('reorderThreshold'),
                onChange: (v) => setNumberField('reorderThreshold', v)
            }
        },
        {
            comp: Forms.Select, name: 'condition', label: t('condition'), required: true,
            props: {options: conditionOpts, disabled: disableWhenEdit}
        },
        {
            comp: Forms.Select, name: 'quality', label: t('quality'), required: true,
            props: {options: qualityOpts, disabled: disableWhenEdit}
        }
    ];

    return (
        <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 mb-4 items-start">
                {fields.map(({comp: Cmp, name, label, required, props}) => (
                    <Cmp
                        key={name}
                        label={label}
                        required={required}
                        value={form[name]}
                        onChange={(val) => {
                            if (props?.onChange) props.onChange(val);
                            else setField(name, val);
                        }}
                        className="rounded-md"
                        {...props}
                    />
                ))}
            </div>

            {/* Očekávaná nákupní cena */}
            <div className="mt-4 p-3 rounded-md border">
                <div className="font-medium mb-2">{t('expectedPurchasePricePerUnit')}</div>
                <div className="grid grid-cols-4 gap-4">
                    <Forms.Select
                        label={t('currency')}
                        value={CURRENCY_OPTIONS.find(o => o.value === form.expectedPurchasePricePerUnit.currency)}
                        options={CURRENCY_OPTIONS}
                        onChange={(opt) => setExpectedPrice({currency: opt?.value || 'CZK'})}
                    />
                    <Forms.Number
                        label={t('net')}
                        placeholder="0.00"
                        value={form.expectedPurchasePricePerUnit.net}
                        onChange={onChangeNet}
                    />
                    <Forms.Number
                        label={t('vatRate')}
                        placeholder="21"
                        value={form.expectedPurchasePricePerUnit.vatRate}
                        onChange={onChangeVat}
                    />
                    <Forms.Number
                        label={t('gross')}
                        placeholder="0.00"
                        value={form.expectedPurchasePricePerUnit.gross}
                        onChange={onChangeGross}
                    />
                </div>
            </div>

            {error && <Error error={error}/>}
            <Forms.Button submit label={isEdit ? t('update') : t('save')} className="mt-4"/>
        </form>
    );
};

export default PartModal;
